package com.example.readingnotes.resources

/**
 * String resources for internationalization (i18n)
 *
 * This class provides centralized string management with support for multiple languages.
 * Currently supports Japanese (default) and English.
 *
 * Usage:
 * ```
 * Text(Strings.Auth.emailRequired)
 * ```
 */
object Strings {
    /**
     * Authentication related strings
     */
    object Auth {
        // Screen titles
        val login = "ログイン"
        val register = "新規登録"
        val totpSetup = "二要素認証の設定"
        val totpVerify = "二要素認証"
        val emailLogin = "メールでログイン"

        // Labels
        val username = "ユーザー名"
        val usernameHint = "6〜20文字の英数字"
        val password = "パスワード"
        val passwordHint = "8文字以上。大文字・小文字・数字・記号を組み合わせてください"
        val confirmPassword = "パスワード（確認）"
        val email = "メールアドレス"
        val verificationCode = "認証コード（6桁）"
        val totpCode = "認証コード"

        // Placeholders
        val emailPlaceholder = "example@email.com"
        val codePlaceholder = "123456"
        val usernamePlaceholder = "username"
        val passwordPlaceholder = "********"

        // Buttons
        val loginButton = "ログイン"
        val registerButton = "登録する"
        val sendCodeButton = "認証コードを送信"
        val verifyButton = "確認"
        val verifyTotpButton = "有効化する"
        val newRegistrationButton = "新規登録"
        val resendButton = "再送する"
        val backButton = "← 戻る"
        val skipButton = "後で設定する"

        // Loading states
        val loginLoading = "ログイン中..."
        val registerLoading = "登録中..."
        val sendCodeLoading = "送信中..."
        val verifyLoading = "認証中..."
        val verifyTotpLoading = "確認中..."
        val loading = "読み込み中..."

        // Hints and descriptions
        val loginDescription = "初めての方は「新規登録」、既存ユーザーの方は「ログイン」を選択してください"
        val emailLoginDescription = "登録されたメールアドレスに認証コードを送信します"
        val emailVerifyDescription = "送信された6桁の認証コードを入力してください"
        val totpVerifyDescription = "認証アプリに表示されている6桁のコードを入力してください"
        val totpSetupDescription = "認証アプリ（Google Authenticator等）で以下のQRコードをスキャンするか、シークレットキーを手動で入力してください。"
        val qrCodeScanHint = "QRコードを認証アプリでスキャン"
        val manualEntryHint = "またはシークレットキーを手動で入力:"
        val totpVerifyLabel = "認証コードを入力して確認"
        val codeExpiryHint = "有効期限は15分です"
        val securePasswordHint = "安全なパスワードを入力してください"
        val confirmPasswordHint = "同じパスワードを再度入力してください"
        val alphanumericOnlyHint = "半角英数字で入力してください"

        // Validation messages (success)
        val validEmail = "有効なメールアドレスです"
        val validUsername = "有効なユーザー名です"
        val validPassword = "有効なパスワードです"
        val validCode = "有効な認証コードです"
        val passwordMatch = "パスワードが一致しました"

        // Validation messages (error)
        val emailRequired = "メールアドレスを入力してください"
        val emailInvalid = "有効なメールアドレスを入力してください"
        val usernameRequired = "ユーザー名を入力してください"
        val usernameTooShort = "6文字以上で入力してください"
        val usernameTooLong = "20文字以下で入力してください"
        val usernameAlphanumericOnly = "英数字のみ使用できます"
        val passwordRequired = "パスワードを入力してください"
        val passwordTooShort = "パスワードは8文字以上で入力してください"
        val passwordMismatch = "パスワードが一致しません"
        val codeRequired = "認証コードを入力してください"
        val codeInvalid = "6桁の数字を入力してください"
        val codeExactLength = "6桁の数字を入力してください"

        // Success messages
        val registerSuccess = "登録に成功しました！"
        val codeSentSuccess = "認証コードを送信しました。メールをご確認ください。"

        // Error messages (placeholder - should be replaced with backend error codes)
        val loginFailed = "ログインに失敗しました"
        val registerFailed = "登録に失敗しました"
        val invalidCredentials = "ユーザー名またはパスワードが間違っています"
        val userAlreadyExists = "このユーザー名は既に使用されています"
        val emailAlreadyExists = "このメールアドレスは既に登録されています"
        val invalidCode = "認証コードが間違っています"
        val codeExpired = "認証コードの有効期限が切れています"
        val totpRequired = "二要素認証が必要です"
        val invalidTotp = "認証コードが間違っています"
        val accountLocked = "アカウントがロックされています"
        val tooManyAttempts = "試行回数が多すぎます。しばらく待ってください"

        // Countdown
        val resendCountdown = "再送 ({0}秒)"

        // Links
        val noAccount = "アカウントをお持ちでない方は "
        val registerLink = "新規登録"
        val orText = "または "
        val emailLoginLink = "メールでログイン"

        // ARIA labels
        val ariaUsername = "ユーザー名"
        val ariaPassword = "パスワード"
        val ariaEmail = "メールアドレス"
        val ariaVerificationCode = "認証コード"
        val ariaTotpCode = "二要素認証コード"
        val ariaTotpSetup = "TOTPセットアップ認証コード"
    }

    /**
     * Common UI strings
     */
    object Common {
        val error = "エラー"
        val success = "成功"
        val warning = "警告"
        val info = "情報"
        val loading = "読み込み中..."
        val retry = "再試行"
        val cancel = "キャンセル"
        val confirm = "確認"
        val submit = "送信"
        val close = "閉じる"
        val save = "保存"
        val delete = "削除"
        val edit = "編集"
    }

    /**
     * Error code to message mapping
     *
     * This maps backend error codes to localized messages
     */
    object ErrorCodes {
        private val errorMessages =
            mapOf(
                "VALIDATION_ERROR" to Auth.loginFailed,
                "UNAUTHORIZED" to Auth.invalidCredentials,
                "FORBIDDEN" to "アクセス権限がありません",
                "NOT_FOUND" to "リソースが見つかりません",
                "DUPLICATE_USERNAME" to Auth.userAlreadyExists,
                "TOTP_REQUIRED" to Auth.totpRequired,
                "TOTP_INVALID" to Auth.invalidTotp,
                "ACCOUNT_LOCKED" to Auth.accountLocked,
                "INTERNAL_ERROR" to "サーバーエラーが発生しました。しばらく待ってから再試行してください",
            )

        /**
         * Get error message for error code
         */
        fun getMessage(
            code: String?,
            default: String? = null,
        ): String = errorMessages[code] ?: default ?: Auth.loginFailed
    }
}
